/**
 * Admin save-time voice style upload helpers.
 * Learners never run this pipeline — they stream hosted playback URLs only.
 */
import { ContentBlockType, type CreateUpdateContentBlockDto } from '../services/contentService';
import type { VoiceStylePreset } from '../services/voiceStylePresetService';
import { voiceStylePresetService } from '../services/voiceStylePresetService';
import type { StickyAudioEntry } from './stickyAudio';
import {
  applyVoiceStyleSettings,
  fetchAudioBlobFromUrl,
  resolveOriginalBlockAudioBlob
} from './voiceThemeProcessing';

export function isBlobUrl(url?: string | null): boolean {
  return !!url && url.startsWith('blob:');
}

/**
 * Legacy blocks only stored AudioUrl. When no stylization preset was applied,
 * that file is the original recording and can be reused for voice stylization.
 */
export function backfillLegacyBlockOriginalAudio(
  block: CreateUpdateContentBlockDto
): CreateUpdateContentBlockDto {
  const isAudioBlock =
    block.type === ContentBlockType.Audio || block.type === ContentBlockType.TextAudio;
  if (
    !isAudioBlock ||
    block.originalAudioUrl ||
    !block.audioUrl ||
    isBlobUrl(block.audioUrl) ||
    block.voiceStylePresetId
  ) {
    return block;
  }
  return { ...block, originalAudioUrl: block.audioUrl };
}

export function normalizeVoicePresetId(presetId?: string | null): string | null {
  if (!presetId || presetId === 'original') return null;
  return presetId;
}

export interface StickyEntryBaseline {
  voiceStylePresetId: string | null;
  originalAudioUrl?: string;
  audioUrl: string;
}

export function snapshotStickyEntryBaselines(
  blocks: Array<{ stickyAudio?: string | null }> | undefined
): Map<string, StickyEntryBaseline> {
  const map = new Map<string, StickyEntryBaseline>();
  if (!blocks) return map;
  for (const block of blocks) {
    if (!block.stickyAudio) continue;
    try {
      const parsed = JSON.parse(block.stickyAudio) as StickyAudioEntry[];
      if (!Array.isArray(parsed)) continue;
      for (const entry of parsed) {
        if (!entry?.id) continue;
        map.set(entry.id, {
          voiceStylePresetId: entry.voiceStylePresetId ?? null,
          originalAudioUrl: entry.originalAudioUrl,
          audioUrl: entry.audioUrl
        });
      }
    } catch {
      // ignore invalid json
    }
  }
  return map;
}

export interface StickyVoiceStyleProcessContext {
  entry: StickyAudioEntry;
  presetId: string;
  savedPresetId: string | null;
  baselineOriginalUrl?: string;
  baselineAudioUrl?: string;
  pendingBlob?: Blob;
  presets: VoiceStylePreset[];
  uploadOriginal: (blob: Blob) => Promise<string>;
  uploadPlayback: (blob: Blob) => Promise<string>;
}

export interface StickyVoiceStyleProcessResult {
  audioUrl: string;
  originalAudioUrl: string;
  voiceStylePresetId: string | null;
}

export async function processStickyEntryVoiceStyle(
  ctx: StickyVoiceStyleProcessContext
): Promise<StickyVoiceStyleProcessResult | null> {
  const {
    entry,
    presetId,
    savedPresetId,
    baselineOriginalUrl,
    pendingBlob,
    presets,
    uploadOriginal,
    uploadPlayback
  } = ctx;

  const targetPresetId = normalizeVoicePresetId(presetId);
  const normalizedSaved = normalizeVoicePresetId(savedPresetId);
  const hasPendingOriginal = !!pendingBlob && isBlobUrl(entry.audioUrl);
  const presetChanged = targetPresetId !== normalizedSaved;

  if (!hasPendingOriginal && !presetChanged) {
    return null;
  }

  if (!hasPendingOriginal && isBlobUrl(entry.audioUrl)) {
    return null;
  }

  const effectiveOriginalUrl = entry.originalAudioUrl ?? baselineOriginalUrl;

  const originalBlob = await resolveOriginalBlockAudioBlob({
    originalAudioUrl: effectiveOriginalUrl,
    previewAudioUrl:
      isBlobUrl(entry.audioUrl) && !effectiveOriginalUrl ? entry.audioUrl : undefined,
    getRecordedBlob: () => pendingBlob
  });

  if (!originalBlob) {
    throw new Error(`Could not read original sticky audio for "${entry.phrase}"`);
  }

  let originalUrl = effectiveOriginalUrl;
  if (hasPendingOriginal) {
    originalUrl = await uploadOriginal(originalBlob);
  } else if (!originalUrl) {
    if (normalizedSaved) {
      throw new Error(
        `Original recording was not stored for sticky phrase "${entry.phrase}". Re-record the clip to change the voice style.`
      );
    }
    originalUrl = entry.audioUrl;
    if (!originalUrl || isBlobUrl(originalUrl)) {
      throw new Error(`Original sticky audio URL is missing for "${entry.phrase}"`);
    }
  }

  if (!originalUrl) {
    throw new Error(`Original sticky audio URL is missing for "${entry.phrase}"`);
  }

  if (!targetPresetId) {
    return {
      audioUrl: originalUrl,
      originalAudioUrl: originalUrl,
      voiceStylePresetId: null
    };
  }

  const preset = voiceStylePresetService.findPreset(presets, targetPresetId);
  if (!preset) {
    return {
      audioUrl: originalUrl,
      originalAudioUrl: originalUrl,
      voiceStylePresetId: null
    };
  }

  let sourceBlob = originalBlob;
  if (!hasPendingOriginal && originalUrl && !isBlobUrl(originalUrl)) {
    sourceBlob = await fetchAudioBlobFromUrl(originalUrl);
  }

  const styledBlob = await applyVoiceStyleSettings(sourceBlob, preset.settings);
  const playbackUrl = await uploadPlayback(styledBlob);

  return {
    audioUrl: playbackUrl,
    originalAudioUrl: originalUrl,
    voiceStylePresetId: targetPresetId
  };
}

export interface BlockVoiceStyleUploadContext {
  blockIndex: number;
  block: CreateUpdateContentBlockDto;
  presetId: string;
  presets: VoiceStylePreset[];
  getRecordedBlob: () => Blob | undefined;
  getUploadedFile: () => File | undefined;
  uploadOriginal: (blob: Blob) => Promise<string>;
  uploadPlayback: (blob: Blob) => Promise<string>;
}

export interface BlockVoiceStyleUploadResult {
  audioUrl: string;
  originalAudioUrl?: string;
  voiceStylePresetId?: string | null;
}

export async function uploadBlockVoiceStyleAudio(
  ctx: BlockVoiceStyleUploadContext
): Promise<BlockVoiceStyleUploadResult | null> {
  const { block, presetId, presets, getRecordedBlob, getUploadedFile, uploadOriginal, uploadPlayback } =
    ctx;

  const targetPresetId = presetId === 'original' ? null : presetId;
  const savedPresetId = block.voiceStylePresetId ?? null;

  const hasPendingOriginal =
    !!getRecordedBlob() ||
    !!getUploadedFile() ||
    isBlobUrl(block.originalAudioUrl) ||
    isBlobUrl(block.audioUrl);
  const presetChanged = targetPresetId !== savedPresetId;

  if (!hasPendingOriginal && !presetChanged) {
    return null;
  }

  const originalBlob = await resolveOriginalBlockAudioBlob({
    originalAudioUrl: block.originalAudioUrl,
    previewAudioUrl: block.audioUrl,
    getRecordedBlob,
    getUploadedFile
  });

  if (!originalBlob) {
    throw new Error('Could not read original audio');
  }

  let originalUrl = block.originalAudioUrl;
  if (hasPendingOriginal) {
    originalUrl = await uploadOriginal(originalBlob);
  } else if (!originalUrl) {
    if (block.voiceStylePresetId) {
      throw new Error(
        'Original recording was not stored for this block. Re-record or upload audio to change the voice style.'
      );
    }
    originalUrl = block.audioUrl;
    if (!originalUrl || isBlobUrl(originalUrl)) {
      throw new Error('Original audio URL is missing');
    }
  }

  if (!originalUrl) {
    throw new Error('Original audio URL is missing');
  }

  if (!targetPresetId) {
    return {
      audioUrl: originalUrl,
      originalAudioUrl: originalUrl,
      voiceStylePresetId: null
    };
  }

  const preset = voiceStylePresetService.findPreset(presets, targetPresetId);
  if (!preset) {
    return {
      audioUrl: originalUrl,
      originalAudioUrl: originalUrl,
      voiceStylePresetId: null
    };
  }

  let sourceBlob = originalBlob;
  if (!hasPendingOriginal && originalUrl && !isBlobUrl(originalUrl)) {
    sourceBlob = await fetchAudioBlobFromUrl(originalUrl);
  }

  const styledBlob = await applyVoiceStyleSettings(sourceBlob, preset.settings);
  const playbackUrl = await uploadPlayback(styledBlob);

  return {
    audioUrl: playbackUrl,
    originalAudioUrl: originalUrl,
    voiceStylePresetId: targetPresetId
  };
}

export function getPresetLabel(presets: VoiceStylePreset[], presetId?: string | null): string {
  if (!presetId || presetId === 'original') return 'Original';
  return presets.find((p) => p.id === presetId)?.name ?? 'Style';
}
